import { createApp } from "vue";
import App from "./RgApp.vue";
import router from "@/router/WebRgRouter";

import {
    AuthPluginVue,
    devLogger,
    InputPluginVue,
    NavbarPluginVue,
    ServerApiPluginVue,
    SessionPluginVue,
    SetupAppGui,
    STLLibraryApiPluginVue,
    GUIConfigurationPluginInstaller,
    isIOSDevice,
    useAppSettings,
} from "@xlabmedical/medic-web-framework";
import { createPinia } from "pinia";

import "primeicons/primeicons.css";

import "@xlabmedical/medic-web-framework/public/assets/fonts.css";
import "@/assets/styles.scss";
import "@/assets/output.css";
import "@/assets/tailwind.css";

import { createAxiosInstance, createAxiosInstanceWithoutURLencoding, ServerAPI, STLLibrary } from "@xlabmedical/medic-api";
import WebRgManagerPlugin from "@/plugins/WebRgManagerPlugin";
import { getEnv, GlobalConstants, isDevelopment } from "@/constants/GlobalConstants";

import DialogService from "primevue/dialogservice";
import ConfirmationService from "primevue/confirmationservice";
import ToastService from "primevue/toastservice";

import * as Sentry from "@sentry/vue";
import { wasmIntegration } from "@sentry/wasm";
import { WASM_BUILD_TYPE, WasmEnginePluginVue } from "@xlabmedical/medic-web-engine";
import { getI18n } from "@/plugins/vueI18n";
import { translation } from "@/util/WebRgLocalizationPlugin";
import { initI18n as initPlannerI18n, setImplantPlannerEnvironment, setImplantIsDevelopment } from "@xlabmedical/implant-planner";
import { initI18n as initEngineI18n } from "@xlabmedical/medic-web-engine";
import { initI18n as initMedicWebFrameworkI18n } from "@xlabmedical/medic-web-framework";
import { memory64, jspi } from "wasm-feature-detect";

console.log(
    `RealGUIDE WebPlanner BuildID: ${GlobalConstants.BUILD_NUMBER} Environment: ${GlobalConstants.ENVIRONMENT} WASM Build Type: ${WASM_BUILD_TYPE} process.env.NODE_ENV: ${process.env.NODE_ENV}`,
);

const appSettingsUrl = new URL("../../medic-web-framework/public/resources/app-settings.json", import.meta.url).toString();
(globalThis as { __APP_SETTINGS_URL?: string }).__APP_SETTINGS_URL = appSettingsUrl;
devLogger.debug("App settings override URL", appSettingsUrl);

const axiosInstance = createAxiosInstance(GlobalConstants.BASE_API_URL);
const axiosInstanceNoTransform = createAxiosInstanceWithoutURLencoding(GlobalConstants.BASE_API_URL);
const app = createApp(App);

setImplantPlannerEnvironment(GlobalConstants.ENVIRONMENT);
setImplantIsDevelopment(isDevelopment());

// const sentryEnabled = !isDevelopment() && ["beta", "sigma"].includes(GlobalConstants.ENVIRONMENT);
// const sentryEnabled = !isDevelopment() || ["beta", "sigma"].includes(GlobalConstants.ENVIRONMENT);
const sentryEnabled = false;
console.log("Sentry enabled", sentryEnabled);
// setting undefined DSN disables Sentry functionality
if (sentryEnabled) {
    const sentryDsn = sentryEnabled ? "https://280960b294ab5699f16e334401291e98@o4505199589326848.ingest.us.sentry.io/4506910273765376" : undefined;
    Sentry.init({
        app,
        dsn: sentryDsn,
        enableLogs: true,
        integrations: [
            Sentry.consoleLoggingIntegration({
                levels: ["log", "warn", "error"],
            }),
            Sentry.browserTracingIntegration({
                router,
            }),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
                maskAllInputs: false,
                networkDetailAllowUrls: [/.*/],
                networkCaptureBodies: true,
                networkRequestHeaders: ["X-Custom-Header"],
                networkResponseHeaders: ["X-Custom-Header"],
            }),
            Sentry.replayCanvasIntegration(),
            wasmIntegration(),
            // Sentry.feedbackIntegration({
            //     // Additional SDK configuration goes in here, for example:
            //     colorScheme: "system",
            // }),
        ],
        attachStacktrace: true,
        environment: getEnv().PROD ? "production" : "development",
        tracesSampleRate: 0.1, //  Capture 100% of the transactions
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        beforeSendLog(log) {
            if (log.message?.includes("WARNING: using emscripten GL emulation. This is a collection of limited")) return null;
            if (log.message?.includes("[Vue warn]")) return null;
            return log;
        },
        beforeBreadcrumb(breadcrumb, hint) {
            if (breadcrumb.category === "console" && breadcrumb.level !== "error") {
                return null;
            }
            if (breadcrumb.message?.includes("WARNING: using emscripten GL emulation. This is a collection of limited")) return null;
            return breadcrumb;
        },
        // beforeSend(event, hint) {
        //     const error = hint.originalException;
        //     if (error && error.message && error.message.match(/database unavailable/i)) {
        //         event.fingerprint = ["database-unavailable"];
        //     }
        //     return event;
        // },
    });
    Sentry.setTags({
        "web-rg:build_number": GlobalConstants.BUILD_NUMBER,
        "web-rg:environment": GlobalConstants.ENVIRONMENT,
        "web-rg:base_url": GlobalConstants.BASE_URL,
        "web-rg:base_api_url": GlobalConstants.BASE_API_URL,
        "web-rg:websocket_api_url": GlobalConstants.WEBSOCKET_API_URL,
    });
}

const api = new ServerAPI(axiosInstance, axiosInstanceNoTransform);
const PiniaPlugin = createPinia();
PiniaPlugin.use(() => {
    return {
        api,
        stlApi: new STLLibrary.STLLibraryAPI(axiosInstance),
    };
});

app.use(AuthPluginVue, { axiosInstance, authSessionKey: undefined });
app.use(DialogService);
app.use(ConfirmationService);
app.use(ToastService);
app.use(ServerApiPluginVue, api);
app.use(STLLibraryApiPluginVue, { axiosInstance });
app.use(SessionPluginVue, { axiosInstances: [axiosInstance, axiosInstanceNoTransform] });
app.use(GUIConfigurationPluginInstaller);
app.use(PiniaPlugin);
app.use(InputPluginVue);
app.use(WebRgManagerPlugin, GlobalConstants.WEBSOCKET_API_URL);
app.use(NavbarPluginVue);

//delete when light mode
localStorage.setItem("colorScheme", '"dark"');

const path = import.meta.env.BASE_URL;
SetupAppGui(app, PiniaPlugin, {
    assetPath: path,
});
app.use(router);

let memory64Supported = false;
let jspiSupported = false;

const isSafariUserAgent = (ua: string) => /Safari/.test(ua) && !/Chrome|Chromium|Android/.test(ua);
const getSafariMajorVersion = (ua: string): number | null => {
    const match = ua.match(/Version\/(\d+)(?:\.(\d+))?/);
    return match ? Number(match[1]) : null;
};
const isSafariVersion26OrAbove = () => {
    const ua = navigator.userAgent;
    if (!isSafariUserAgent(ua)) {
        return false;
    }
    const major = getSafariMajorVersion(ua);
    return major !== null && major >= 26;
};

// Make isIOS globally available
(window as any).isIOS = function () {
    return isIOSDevice();
};
// Make isSafari globally available
(window as any).isSafari = function () {
    return isSafariVersion26OrAbove();
};

(async () => {
    const { loadAppSettings: ensureAppSettings } = useAppSettings();
    await ensureAppSettings();

    if (await memory64()) {
        memory64Supported = true;
        console.log("Memory64 supported");
    } else {
        console.log("Memory64 not supported");
    }
    if (await jspi()) {
        jspiSupported = true;
        console.log("jspi supported");
    } else {
        console.log("jspi not supported");
    }

    app.use(WasmEnginePluginVue, {
        assetPath: path,
        wasm64: memory64Supported && jspiSupported,
    });

    // register i18n translations for web-rg, planner and web-engine
    const i18n = getI18n();
    app.use(i18n);
    initPlannerI18n(i18n);
    initEngineI18n(i18n);
    initMedicWebFrameworkI18n(i18n);

    app.mount("#app");
})();

// Minimal detection (covers old+new API spellings)
(window as any).hasJSPI = function () {
    return jspiSupported;
};
(window as any).hasMemory64 = function () {
    return memory64Supported;
};

(window as any).baseWasm = function () {
    return !memory64Supported || !jspiSupported;
};
